// src/Screens/SIAFlightMapScreen.js
import React, { useState, useEffect, useMemo } from 'react';
import { useFlight } from '../Contexts/FlightContext';
import { useAuth } from '../Contexts/AuthContext';
import FlightMap from '../Components/FlightMap';
import RouteDetails from '../Components/RouteDetails';
import StatsBar from '../Components/StatsBar';
import Controls from '../Components/Controls';
import LoadingState from '../Components/LoadingState';
import ErrorState from '../Components/ErrorState';
import SideNav from '../Components/SideNav';
import PaywallModal from '../Components/PaywallModal';
import FleetInfoModal from '../Components/FleetInfoModal';
import ModelInfoModal from '../Components/ModelInfoModal';
import InfoModal from '../Components/InfoModal';
import ManageSubscriptionModal from '../Components/ManageSubscriptionModal';
import Toast from '../Components/Toast';
import Footer from '../Components/Footer';

export default function SIAFlightMapScreen() {
  const { 
    filteredFlights, 
    loading, 
    error, 
    filterStatus, 
    setFilterStatus, 
    filterAircraftModel,
    setFilterAircraftModel,
    filterDataset,
    setFilterDataset,
    aircraftModels,
    refetch, 
    flights 
  } = useFlight();
  
  const { user, isPremium } = useAuth();
  
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [mapCenter, setMapCenter] = useState([1.3644, 103.9915]);
  const [mapZoom, setMapZoom] = useState(4);
  const [statsHeight, setStatsHeight] = useState(0);
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [fleetInfoOpen, setFleetInfoOpen] = useState(false);
  const [modelInfoOpen, setModelInfoOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);

  // Set default dataset based on premium status
  useEffect(() => {
    if (isPremium) {
      setFilterDataset('10days');
    } else {
      setFilterDataset('5days');
    }
  }, [isPremium, setFilterDataset]);

  // Calculate stats based on filtered flights
  const filteredStats = useMemo(() => {
    if (!filteredFlights || filteredFlights.length === 0) {
      return {
        totalFlights: 0,
        uniqueRoutes: 0,
        uniqueAircraft: 0,
        destinations: 0
      };
    }

    const uniqueRouteSet = new Set();
    const uniqueAircraftSet = new Set();
    const destinationSet = new Set();

    filteredFlights.forEach(flight => {
      // Create unique route identifier
      if (flight.origin && flight.destination) {
        uniqueRouteSet.add(`${flight.origin}-${flight.destination}`);
      }
      
      // Track unique aircraft
      if (flight.registration) {
        uniqueAircraftSet.add(flight.registration);
      }
      
      // Track destinations
      if (flight.destination) {
        destinationSet.add(flight.destination);
      }
    });

    return {
      totalFlights: filteredFlights.length,
      uniqueRoutes: uniqueRouteSet.size,
      uniqueAircraft: uniqueAircraftSet.size,
      destinations: destinationSet.size
    };
  }, [filteredFlights]);
  const [manageSubOpen, setManageSubOpen] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  // Check for payment success/cancel in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      // User returned from Stripe Checkout
      setToast({
        visible: true,
        message: 'Payment processing... Your premium features will be activated shortly!',
        type: 'success'
      });
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Show success toast when premium status changes
  useEffect(() => {
    if (isPremium && user) {
      const hasShownWelcome = sessionStorage.getItem('premiumWelcomeShown');
      if (!hasShownWelcome) {
        setToast({
          visible: true,
          message: '🎉 Welcome to Premium! All features are now unlocked.',
          type: 'success'
        });
        sessionStorage.setItem('premiumWelcomeShown', 'true');
      }
    }
  }, [isPremium, user]);

  // Force map update when flights load
  useEffect(() => {
    if (filteredFlights.length > 0) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  }, [filteredFlights.length]);

  // Handler for when user clicks on an aircraft model badge
  const handleModelClick = (modelName) => {
    setSelectedModel(modelName);
    setModelInfoOpen(true);
  };

  // Handler for when ModelInfoModal closes
  const handleModelInfoClose = () => {
    setModelInfoOpen(false);
    setSelectedModel(null);
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className="w-full h-full relative bg-gray-100 overflow-hidden" style={{ height: '100dvh', maxHeight: '100dvh' }}>
      {/* Fullscreen Map Container */}
      <div className="absolute inset-0 z-0">
        <FlightMap 
          flights={filteredFlights}
          selectedRoute={selectedRoute}
          setSelectedRoute={setSelectedRoute}
          mapCenter={mapCenter}
          mapZoom={mapZoom}
          setMapCenter={setMapCenter}
          setMapZoom={setMapZoom}
          filterStatus={filterStatus}
        />
      </div>

      {/* Route Details - Outside map container to ensure proper z-index layering */}
      <RouteDetails 
        route={selectedRoute}
        onClose={() => setSelectedRoute(null)}
        isPremium={isPremium}
        onPremiumFeatureClick={() => setPaywallOpen(true)}
        onModelClick={handleModelClick}
      />
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40">
        <div
          className="flex items-center justify-between px-4 sm:px-5"
          style={{
            height: '56px',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 12px rgba(0,0,0,0.06)'
          }}
        >
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#1d4ed8">
              <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
            </svg>
            <div className="min-w-0">
              <h1 className="font-semibold text-sm sm:text-base leading-tight truncate" style={{ color: '#111111', letterSpacing: '-0.01em' }}>
                SIA Flight Tracker
              </h1>
              <p className="text-xs leading-tight hidden sm:block" style={{ color: '#6b7280' }}>
                Singapore Airlines Route Network
              </p>
            </div>
          </div>

          {/* Center: Live indicator */}
          <div className="flex items-center gap-1.5 px-3">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" style={{ animation: 'pulse 2s infinite' }}></span>
            <span className="text-xs font-medium" style={{ color: '#6b7280', letterSpacing: '0.05em' }}>LIVE</span>
          </div>

          {/* Right: Info + Menu */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setInfoModalOpen(true)}
              className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              style={{ width: '34px', height: '34px' }}
              aria-label="Information"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="8" strokeWidth="2.5"/>
                <line x1="12" y1="12" x2="12" y2="16"/>
              </svg>
            </button>
            <button
              onClick={() => setSideNavOpen(true)}
              className="flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              style={{ width: '34px', height: '34px' }}
              aria-label="Open menu"
            >
              {user ? (
                <div className="relative">
                  <img
                    src={user.photoURL}
                    alt={user.displayName}
                    className="rounded-full object-cover"
                    style={{ width: '28px', height: '28px' }}
                  />
                  {isPremium && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 border border-white rounded-full"></span>
                  )}
                </div>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar - Now at top */}
      <StatsBar 
        stats={filteredStats} 
        topOffset={64} 
        onHeightChange={setStatsHeight}
      />
      
      {/* Controls - Now below Stats */}
      <Controls 
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterAircraftModel={filterAircraftModel}
        setFilterAircraftModel={setFilterAircraftModel}
        filterDataset={filterDataset}
        setFilterDataset={setFilterDataset}
        aircraftModels={aircraftModels}
        filteredCount={filteredFlights.length}
        totalCount={flights.length}
        isPremium={isPremium}
        onPremiumFeatureClick={() => setPaywallOpen(true)}
        topOffset={64 + statsHeight + 12}
      />

      <SideNav 
        isOpen={sideNavOpen}
        onClose={() => setSideNavOpen(false)}
        onOpenPaywall={() => setPaywallOpen(true)}
        onOpenFleetInfo={() => setFleetInfoOpen(true)}
        onOpenModelInfo={() => setModelInfoOpen(true)}
        onManageSubscription={() => setManageSubOpen(true)}
      />

      <PaywallModal 
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
      />

      <FleetInfoModal
        isOpen={fleetInfoOpen}
        onClose={() => setFleetInfoOpen(false)}
      />

      <ModelInfoModal
        isOpen={modelInfoOpen}
        onClose={handleModelInfoClose}
        model={selectedModel}
        isPremium={isPremium}
        onPremiumFeatureClick={() => setPaywallOpen(true)}
      />

      <InfoModal
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        />

      <ManageSubscriptionModal 
        isOpen={manageSubOpen}
        onClose={() => setManageSubOpen(false)}
      />

      <Toast 
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast({ ...toast, visible: false })}
      />
      <Footer />
    </div>
  );
}